import { useEffect, useState, useRef } from 'react';
import { Progress } from '@/components/ui/progress';

interface ProgressBarProps {
  expectedCompleteDurationSeconds: number;
  bufferDelaySeconds?: number; // Optional buffer delay parameter
}

export function PolicyExtractorProgressBar({
  expectedCompleteDurationSeconds,
  bufferDelaySeconds = 1.5, // Default 1.5 second buffer. Prevents the progress bar from resetting too quickly.
}: ProgressBarProps) {
  const [progress, setProgress] = useState(0);
  const intervalIdRef = useRef<number | null>(null);
  const lastTimestampRef = useRef<number | null>(null);
  const totalDurationMs = expectedCompleteDurationSeconds * 1000;

  const bufferDelayMs = bufferDelaySeconds * 1000;

  useEffect(() => {
    // Clear any existing animation frame when the component mounts or when duration changes
    if (intervalIdRef.current !== null) {
      cancelAnimationFrame(intervalIdRef.current);
    }

    const startTime = performance.now();
    let animationStarted = false;

    const updateProgress = (timestamp: number) => {
      if (lastTimestampRef.current === null) {
        lastTimestampRef.current = startTime;
      }

      const elapsedMs = timestamp - startTime;

      // Only start animation after buffer delay
      if (!animationStarted && elapsedMs < bufferDelayMs) {
        intervalIdRef.current = requestAnimationFrame(updateProgress);
        return;
      }

      animationStarted = true;

      // Adjust elapsed time to account for buffer delay
      const adjustedElapsedMs = elapsedMs - bufferDelayMs;
      const newProgress = Math.min(99, (adjustedElapsedMs / totalDurationMs) * 99);

      setProgress(newProgress);

      if (newProgress < 99) {
        intervalIdRef.current = requestAnimationFrame(updateProgress);
      }
    };

    intervalIdRef.current = requestAnimationFrame(updateProgress);

    return () => {
      if (intervalIdRef.current !== null) {
        cancelAnimationFrame(intervalIdRef.current);
      }
    };
  }, [expectedCompleteDurationSeconds, totalDurationMs, bufferDelayMs]);

  return (
    <div className="mb-2 mt-4 flex flex-col gap-2 md:mb-0 md:mt-1">
      <div className="flex justify-between">
        <p className="text-xs text-gray-500">Extracting policy data...</p>
        <p className="text-xs text-gray-500">{Math.round(progress)}%</p>
      </div>
      <Progress
        value={progress}
        className={`bg-primary-100 h-2 w-full `}
      />
    </div>
  );
}
